import { LegalDocumentPage } from "@/components/legal/LegalDocumentPage";
import { getBrandConfig } from "@/config/brand";

function buildPrivacyMarkdown(brandName: string) {
  return `# ${brandName} - Cloud Privacy Policy

**Effective Date:** March 25, 2026  
**Last Updated:** March 25, 2026  
**Applies To:** Customers using ${brandName} as a WYNI-hosted cloud service ("Cloud Service")

---

## Part B: Cloud Privacy Policy

### 1. Categories of Data Collected and Processed

WYNI may collect and process:

- Account and identity data (name, email, role, workspace/tenant metadata)
- Authentication/session data (token and cookie-related data)
- Meeting uploads, transcript content, and analysis outputs
- Document uploads, OCR results, indexed chunks, and embeddings
- Chat prompts/responses, session metadata, and generated artifacts
- Integration connection metadata and token metadata
- Usage, performance, operational, and security logs
- Limited client-side local storage for user experience continuity

### 2. Purposes of Processing

Data is processed to:

- Provide and operate core cloud features
- Authenticate users and enforce tenant/workspace access controls
- Process AI, search, and retrieval workflows
- Enable user-authorized integrations
- Monitor security, reliability, abuse prevention, and platform performance
- Meet legal and contractual obligations

### 3. Legal Bases (Where Required)

Depending on jurisdiction, processing may rely on:

- Contract performance
- Legitimate interests
- Consent (where required)
- Legal obligations

### 4. Sharing and Subprocessors

Data may be shared with:

- Hosting/infrastructure and database providers
- AI/STT/OCR/search providers used by enabled features
- Third-party integrations explicitly authorized by Customer
- Professional advisors, auditors, or authorities where legally required

WYNI does not sell personal data in the ordinary commercial sense.

### 5. International Transfers

Data may be processed across jurisdictions. WYNI applies appropriate transfer safeguards as required by law.

### 6. Security Measures

WYNI applies reasonable technical and organizational controls, including authentication controls, authorization checks, transport security, monitoring, and operational safeguards.

### 7. Retention and Deletion

Retention periods depend on service purpose, contractual terms, legal obligations, and operational requirements. Data is deleted or anonymized according to applicable retention schedules.

### 8. Data Subject Rights

Where applicable law provides such rights, users may request access, correction, deletion, portability, restriction/objection, and complaint handling. Workspace users should first contact their workspace administrator.

### 9. Children's Data

The service is not intended for children below the age threshold defined by applicable law without a valid legal basis and required consent.

### 10. Policy Updates

WYNI may update this Privacy Policy from time to time. Material changes will be communicated through reasonable channels.

### 11. Contact

Privacy contact: support@wyni.technology (Miss Winnie Leung)`;
}

const PrivacyPolicy = () => {
  const { brandName } = getBrandConfig();
  return (
    <LegalDocumentPage
      title="Privacy Policy"
      description={`How ${brandName} handles cloud-service data collection, processing, sharing, retention, and privacy rights.`}
      content={buildPrivacyMarkdown(brandName)}
    />
  );
};

export default PrivacyPolicy;
